import { useEffect, useRef, useState } from 'react';
import {
  FACTORY_TZ,
  IMPORT_KIND_HINT,
  IMPORT_KIND_LABEL,
  KIND_ROLES,
  ROLE_LABEL_COL,
  localDate,
  type AttrSetting,
  type ColumnRole,
  type ImportKind,
  type ImportPreviewDto,
  type ImportResultDto,
} from '@app/shared';
import { api } from '../api';
import { platform } from '../platform';
import { useApp } from '../state';
import { Icon } from '../ui';
import { SubHead } from './admin/SubHead';

const KINDS: ImportKind[] = ['catalog', 'stock', 'plan', 'fact'];

/**
 * Загрузка таблиц из Excel/1С: номенклатура, остатки, план производства, факт за месяц.
 * Приложение угадывает столбцы, вы подтверждаете или меняете; сопоставление запоминается до следующей загрузки.
 */
export function Imports() {
  const [attrs, setAttrs] = useState<AttrSetting[] | null>(null);
  const { fail } = useApp();
  useEffect(() => {
    api.attrs().then(setAttrs).catch(fail);
  }, [fail]);
  async function toggle(key: string) {
    if (!attrs) return;
    const next = attrs.map((a) => (a.key === key ? { ...a, show: !a.show } : a));
    setAttrs(next);
    try {
      setAttrs(await api.setAttrs(next));
    } catch (e) {
      fail(e);
    }
  }
  function move(i: number, dir: -1 | 1) {
    if (!attrs) return;
    const next = [...attrs];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j]!, next[i]!];
    setAttrs(next);
    void api.setAttrs(next).then(setAttrs).catch(fail);
  }
  return (
    <div className="stack">
      <SubHead title="Загрузка таблиц" />
      <div className="card flat small">Excel (.xlsx) или CSV. Связь между таблицами — по артикулу (SKU), а если его нет — по наименованию.</div>
      {KINDS.map((k) => (
        <ImportBlock key={k} kind={k} onDone={() => (k === 'catalog' ? api.attrs().then(setAttrs).catch(() => {}) : undefined)} />
      ))}
      <h2 className="section-title">Что выводить на карточках</h2>
      {!attrs && <div className="skeleton" />}
      {attrs && attrs.length === 0 && <p className="small muted">Характеристики появятся после загрузки номенклатуры.</p>}
      {attrs && attrs.length > 0 && (
        <div className="list">
          {attrs.map((a, i) => (
            <div key={a.key} className="item">
              <label className="check grow" style={{ margin: 0 }}>
                <input type="checkbox" checked={a.show} onChange={() => void toggle(a.key)} />
                <span>{a.key}</span>
              </label>
              <button type="button" className="icon-btn" aria-label={`Выше: ${a.key}`} disabled={i === 0} onClick={() => move(i, -1)}>
                <Icon name="up" size={16} />
              </button>
              <button type="button" className="icon-btn" aria-label={`Ниже: ${a.key}`} disabled={i === attrs.length - 1} onClick={() => move(i, 1)}>
                <Icon name="down" size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="small muted" style={{ margin: 0 }}>
        Остальные характеристики не видны на карточке, но по ним можно искать и группировать на доске.
      </p>
    </div>
  );
}

function ImportBlock({ kind, onDone }: { kind: ImportKind; onDone: () => void }) {
  const { fail, toast } = useApp();
  const input = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<ImportPreviewDto | null>(null);
  const [roles, setRoles] = useState<ColumnRole[]>([]);
  const [period, setPeriod] = useState(localDate(Date.now(), FACTORY_TZ).slice(0, 7));
  const [result, setResult] = useState<ImportResultDto | null>(null);
  const [busy, setBusy] = useState(false);

  async function pick(f: File | undefined) {
    if (input.current) input.current.value = '';
    if (!f) return;
    setBusy(true);
    setResult(null);
    try {
      const p = await api.importPreview(kind, f);
      setPreview(p);
      setRoles(p.roles);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }
  async function commit() {
    if (!preview) return;
    setBusy(true);
    try {
      const r = await api.importCommit(kind, { token: preview.token, headerRow: preview.headerRow, roles, ...(kind === 'plan' ? { period } : {}) });
      setResult(r);
      setPreview(null);
      platform.haptic('success');
      toast(`${IMPORT_KIND_LABEL[kind]}: загружено`);
      onDone();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card stack" style={{ gap: 8 }}>
      <div className="row">
        <b className="grow">{IMPORT_KIND_LABEL[kind]}</b>
        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => input.current?.click()}>
          <Icon name="upload" size={16} />
          {busy && !preview ? 'Читаю…' : 'Файл'}
        </button>
      </div>
      <input ref={input} type="file" accept=".xlsx,.csv,text/csv" hidden aria-label={`Файл: ${IMPORT_KIND_LABEL[kind]}`} onChange={(e) => void pick(e.target.files?.[0])} />
      <span className="small muted">{IMPORT_KIND_HINT[kind]}</span>
      {preview && (
        <>
          <span className="small">
            Строк: {preview.rows}. Укажите, что в каждом столбце:
          </span>
          <div className="map-table">
            <table>
              <thead>
                <tr>
                  {preview.headers.map((h, i) => (
                    <th key={i}>
                      <div className="small">{h}</div>
                      <select
                        className="input"
                        aria-label={`Столбец «${h}»`}
                        value={roles[i] ?? 'skip'}
                        onChange={(e) => setRoles((r) => r.map((x, j) => (j === i ? (e.target.value as ColumnRole) : x)))}
                      >
                        {KIND_ROLES[kind].map((r) => (
                          <option key={r} value={r}>
                            {ROLE_LABEL_COL[r]}
                          </option>
                        ))}
                      </select>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.sample.map((r, i) => (
                  <tr key={i}>
                    {preview.headers.map((_, j) => (
                      <td key={j} className={roles[j] === 'skip' ? 'muted' : ''}>
                        {r[j] ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {kind === 'plan' && !roles.includes('period') && (
            <div className="field">
              <label htmlFor={`pp-${kind}`}>Месяц плана</label>
              <input id={`pp-${kind}`} className="input" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
            </div>
          )}
          <div className="grid-half">
            <button type="button" className="btn btn-ghost" onClick={() => setPreview(null)}>
              Отмена
            </button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void commit()}>
              Загрузить
            </button>
          </div>
        </>
      )}
      {result && (
        <div className="small" role="status">
          Новых: {result.created}, обновлено: {result.updated}
          {result.skipped ? `, пропущено строк: ${result.skipped}` : ''}.
          {result.unmatched.length > 0 && <div className="muted">Не нашёл в номенклатуре: {result.unmatched.join(', ')}</div>}
        </div>
      )}
    </section>
  );
}
